'use client'

import { useEffect, useState, useRef } from 'react'
import {
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { tracking } from '@/tracking'
import { CookieYesConsentManager } from '@/tracking/consent/CookieYesConsentManager'
import { eventRegistry } from '@/tracking/registry'
import type {
  DestinationKey,
  IdentityState,
  ConsentSnapshot,
} from '@/tracking/types'

interface DebugEvent {
  id: string
  type: 'track' | 'identify' | 'page'
  eventName?: string
  userId?: string
  traits?: Record<string, any>
  path?: string
  properties?: Record<string, any>
  timestamp: Date
  destinations: DestinationKey[]
  consent: ConsentSnapshot
  identity: IdentityState
}

export const TrackingDebugViewer = () => {
  const isDev = process.env.NODE_ENV === 'development'
  const isEnabled = process.env.NEXT_PUBLIC_TRACKING_VIEWER_ENABLED === '1'

  const [events, setEvents] = useState<DebugEvent[]>([])
  const [isOpen, setIsOpen] = useState(true)
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null)
  const [, setCurrentIdentity] = useState<IdentityState>({})
  const [currentConsent, setCurrentConsent] = useState<ConsentSnapshot>({
    functional: false,
    statistics: false,
    marketing: false,
  })

  const consentManagerRef = useRef<CookieYesConsentManager | null>(null)
  const originalTrackingRef = useRef<typeof tracking | null>(null)
  const identityRef = useRef<IdentityState>({})

  useEffect(() => {
    if (!isDev || !isEnabled) {
      return
    }

    // Initialize consent manager
    consentManagerRef.current = new CookieYesConsentManager()
    setCurrentConsent(consentManagerRef.current.getSnapshot())

    const unsubscribe = consentManagerRef.current.onChange(snapshot => {
      setCurrentConsent(snapshot)
    })

    // Store original tracking methods
    originalTrackingRef.current = {
      identify: tracking.identify,
      track: tracking.track,
      page: tracking.page,
      reset: tracking.reset,
    }

    // Wrap tracking methods to intercept calls
    const wrappedTracking = {
      identify: (
        userId: string,
        traits?: Record<string, any>,
        options?: any
      ) => {
        const identity: IdentityState = {
          ...identityRef.current,
          email: userId,
          customerId: traits?.customerId ?? identityRef.current.customerId,
          traits: { ...(identityRef.current.traits ?? {}), ...(traits ?? {}) },
        }
        identityRef.current = identity
        setCurrentIdentity(identity)

        const def = eventRegistry['identify']
        const destinations = def?.defaultDestinations ?? [
          'kissmetrics',
          'ga4',
          'facebookPixel',
          'customerIo',
        ]

        const event: DebugEvent = {
          id: `${Date.now()}-${Math.random()}`,
          type: 'identify',
          userId,
          traits,
          timestamp: new Date(),
          destinations,
          consent: consentManagerRef.current!.getSnapshot(),
          identity,
        }

        setEvents(prev => [...prev, event])

        // Call original
        originalTrackingRef.current!.identify(userId, traits, options)
      },

      track: (
        eventName: string,
        properties?: Record<string, any>,
        options?: any
      ) => {
        const def = eventRegistry[eventName]
        const destinations = def?.defaultDestinations ?? ['kissmetrics']

        const event: DebugEvent = {
          id: `${Date.now()}-${Math.random()}`,
          type: 'track',
          eventName,
          properties,
          timestamp: new Date(),
          destinations,
          consent: consentManagerRef.current!.getSnapshot(),
          identity: identityRef.current,
        }

        setEvents(prev => [...prev, event])

        // Call original
        originalTrackingRef.current!.track(eventName, properties, options)
      },

      page: (path: string, properties?: Record<string, any>, options?: any) => {
        const def = eventRegistry['page']
        const destinations = def?.defaultDestinations ?? [
          'ga4',
          'facebookPixel',
          'customerIo',
        ]

        const event: DebugEvent = {
          id: `${Date.now()}-${Math.random()}`,
          type: 'page',
          path,
          properties,
          timestamp: new Date(),
          destinations,
          consent: consentManagerRef.current!.getSnapshot(),
          identity: identityRef.current,
        }

        setEvents(prev => [...prev, event])

        // Call original
        originalTrackingRef.current!.page(path, properties, options)
      },

      reset: () => {
        identityRef.current = {}
        setCurrentIdentity({})
        originalTrackingRef.current!.reset()
      },
    }

    // Replace tracking methods
    ;(tracking as any).identify = wrappedTracking.identify
    ;(tracking as any).track = wrappedTracking.track
    ;(tracking as any).page = wrappedTracking.page
    ;(tracking as any).reset = wrappedTracking.reset

    return () => {
      // Restore original methods on unmount
      if (originalTrackingRef.current) {
        ;(tracking as any).identify = originalTrackingRef.current.identify
        ;(tracking as any).track = originalTrackingRef.current.track
        ;(tracking as any).page = originalTrackingRef.current.page
        ;(tracking as any).reset = originalTrackingRef.current.reset
      }
      unsubscribe()
    }
  }, [isDev, isEnabled])

  const clearEvents = () => {
    setEvents([])
    setExpandedEventId(null)
  }

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    })
  }

  const formatConsent = (consent: ConsentSnapshot) => {
    const parts: string[] = []
    if (consent.functional) parts.push('functional')
    if (consent.statistics) parts.push('statistics')
    if (consent.marketing) parts.push('marketing')
    return parts.length > 0 ? parts.join(', ') : 'none'
  }

  const getConsentBadgeColor = (consent: ConsentSnapshot) => {
    const hasAny = consent.functional || consent.statistics || consent.marketing
    if (!hasAny) return 'bg-gray-700 text-gray-300'
    if (consent.statistics && consent.marketing)
      return 'bg-green-600 text-green-100'
    if (consent.statistics) return 'bg-yellow-600 text-yellow-100'
    return 'bg-orange-600 text-orange-100'
  }

  const formatIdentity = (identity: IdentityState) => {
    const parts: string[] = []
    if (identity.email) parts.push(`email: ${identity.email}`)
    if (identity.customerId) parts.push(`customerId: ${identity.customerId}`)
    return parts.length > 0 ? parts.join(', ') : 'anonymous'
  }

  if (!isDev || !isEnabled) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)] font-mono text-xs">
      <div className="bg-gray-900 text-gray-100 border-t border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 hover:text-gray-300 transition-colors"
            >
              {isOpen ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronUpIcon className="w-4 h-4" />
              )}
              <span className="font-semibold">Tracking Debug</span>
            </button>
            <span className="text-gray-400">
              {events.length} {events.length === 1 ? 'event' : 'events'}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">CookieYes:</span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-semibold ${getConsentBadgeColor(
                  currentConsent
                )}`}
                title={`Consent: ${formatConsent(currentConsent)}`}
              >
                {formatConsent(currentConsent)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearEvents}
              className="px-2 py-1 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Clear events"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="px-2 py-1 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {isOpen && (
          <div className="max-h-96 overflow-y-auto">
            {events.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No tracking events yet. Interact with the site to see events.
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {events.map(event => {
                  const isExpanded = expandedEventId === event.id
                  return (
                    <div
                      key={event.id}
                      className="bg-gray-900 hover:bg-gray-850"
                    >
                      <button
                        onClick={() =>
                          setExpandedEventId(isExpanded ? null : event.id)
                        }
                        className="w-full px-4 py-2 text-left flex items-center justify-between hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              event.type === 'track'
                                ? 'bg-blue-600 text-blue-100'
                                : event.type === 'identify'
                                  ? 'bg-green-600 text-green-100'
                                  : 'bg-purple-600 text-purple-100'
                            }`}
                          >
                            {event.type.toUpperCase()}
                          </span>
                          <span className="text-gray-200 font-medium truncate">
                            {event.type === 'track'
                              ? event.eventName
                              : event.type === 'identify'
                                ? `identify(${event.userId})`
                                : `page(${event.path})`}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {formatTimestamp(event.timestamp)}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronUpIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-3 space-y-2 bg-gray-800">
                          <div>
                            <div className="text-gray-400 text-xs mb-1">
                              Properties
                            </div>
                            <pre className="bg-gray-900 p-2 rounded text-xs overflow-x-auto">
                              {JSON.stringify(
                                event.type === 'track'
                                  ? event.properties
                                  : event.type === 'identify'
                                    ? event.traits
                                    : event.properties,
                                null,
                                2
                              )}
                            </pre>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-400">
                                Destinations:
                              </span>
                              <span className="ml-2 text-gray-200">
                                {event.destinations.length > 0
                                  ? event.destinations.join(', ')
                                  : 'none'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-400">Consent:</span>
                              <span className="ml-2 text-gray-200">
                                {formatConsent(event.consent)}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-400">Identity:</span>
                              <span className="ml-2 text-gray-200">
                                {formatIdentity(event.identity)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
